import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Modulepage.module.css";
import type { ApiOptions } from "./dashboardApi";
import { apiCall, apiDownload, apiUpload } from "./frontdeskApi";
import type { EndpointDef, FieldDef, ModuleDef, SectionDef } from "../../../types/Catalog";

/* ------------------------------------------------------------------ *
 *  Module page (white theme) – one page per backend module, opened from the Front Desk sidebar.
 *  Every section is driven by the API catalog (catalog.ts, generated from the controllers):
 *    list endpoint  -> searchable table with paging, filters, "New" and the row actions
 *    row click      -> detail drawer (GET by id, the sub-views of the row, edit / delete / actions)
 *    other GETs     -> views (stats, summaries, reports, downloads)
 *    other writes   -> "Actions" menu
 *  Nothing is hard-coded: all data, fields and options come from the backend.
 * ------------------------------------------------------------------ */

export interface ModulePageProps {
  module: ModuleDef;
  api: ApiOptions;
  /** Text from the page header search box. */
  query?: string;
}

type Row = Record<string, unknown>;
type Vals = Record<string, string>;
const PAGE_SIZE = 25;
const AUTO_MS = 15000;

/* ---------- small helpers ---------- */

const isRecord = (v: unknown): v is Row => typeof v === "object" && v !== null && !Array.isArray(v);
const humanize = (s: string) => {
  const t = s
    .replace(/[_\-.]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const placeholders = (path: string): string[] => Array.from(path.matchAll(/\{(\w+)\}/g)).map((m) => m[1] ?? "");
const fillPath = (path: string, vals: Vals) => path.replace(/\{(\w+)\}/g, (_, n: string) => encodeURIComponent(vals[n] ?? ""));
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;
const fmtDate = (v: string) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return v.length <= 10 ? d.toLocaleDateString() : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};
const scalarText = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "–";
  if (typeof v === "string") return ISO.test(v) ? fmtDate(v) : v;
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return JSON.stringify(v);
};
const SECRET = /(secret|apikey|api_key|token|password|signing)/i;
const hasSecret = (v: unknown, depth = 0): boolean => {
  if (depth > 3 || !isRecord(v)) return false;
  return Object.entries(v).some(([k, x]) => (SECRET.test(k) && typeof x === "string" && x.length > 8) || hasSecret(x, depth + 1));
};
const errText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong.");

const PRIORITY = ["name", "displayName", "title", "label", "code", "email", "number", "phoneNumber", "e164", "caller", "status", "state", "direction", "type", "kind", "strategy", "isActive", "enabled", "active", "createdAt", "startedAt", "updatedAt"];
const rowTitle = (r: Row): string => {
  for (const k of ["name", "displayName", "title", "label", "code", "email", "number", "phoneNumber", "e164", "subject"]) {
    const v = r[k];
    if (typeof v === "string" && v) return v;
  }
  return typeof r.id === "string" ? r.id : "Details";
};
const pickColumns = (rows: Row[]): string[] => {
  const keys = new Set<string>();
  rows.slice(0, 15).forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
  const scalar = (k: string) => rows.slice(0, 15).some((r) => r[k] !== null && r[k] !== undefined && typeof r[k] !== "object");
  const all = [...keys].filter(scalar);
  const idLike = (k: string) => k === "id" || /Id$/.test(k) || /(Json|Hash|Token|Secret|Url)$/i.test(k);
  const pri = PRIORITY.filter((k) => keys.has(k) && scalar(k));
  const rest = all.filter((k) => !pri.includes(k) && !idLike(k));
  const cols = [...pri, ...rest].slice(0, 7);
  return cols.length > 0 ? cols : all.slice(0, 7);
};

/* ---------- id pickers: fields such as queueId show the queues instead of asking for a GUID ---------- */

const REFS: [string, string][] = [
  ["landingpageid", "/api/landing-pages"],
  ["trackingsiteid", "/api/tracking-sites"],
  ["dispositionid", "/api/dispositions"],
  ["connectionid", "/api/integrations/connections"],
  ["webhookid", "/api/integrations/webhooks"],
  ["campaignid", "/api/campaigns"],
  ["scheduleid", "/api/routing/schedules"],
  ["ivrflowid", "/api/routing/ivr-flows"],
  ["queueid", "/api/queues"],
  ["agentid", "/api/agents"],
  ["teamid", "/api/agent-teams"],
  ["skillid", "/api/agent-skills"],
  ["sourceid", "/api/sources"],
  ["brandid", "/api/brands"],
  ["numberid", "/api/numbers"],
  ["poolid", "/api/number-pools"],
  ["listid", "/api/routing/lists"],
  ["formid", "/api/quality/forms"],
];
const refPath = (name: string): string | null => {
  const n = name.toLowerCase();
  for (const [suffix, path] of REFS) if (n === suffix || n.endsWith(suffix)) return path;
  return null;
};
interface Option {
  id: string;
  label: string;
}
const optionCache = new Map<string, { at: number; items: Option[] }>();
const useOptions = (api: ApiOptions, path: string | null): Option[] | null => {
  const [items, setItems] = useState<Option[] | null>(() => (path ? (optionCache.get(path)?.items ?? null) : null));
  useEffect(() => {
    if (!path) return;
    const hit = optionCache.get(path);
    if (hit && Date.now() - hit.at < 60000) {
      setItems(hit.items);
      return;
    }
    let alive = true;
    apiCall<unknown>(api, "GET", path, { params: { pageSize: 200 } })
      .then((d) => {
        const list = Array.isArray(d) ? d : isRecord(d) && Array.isArray(d.items) ? d.items : [];
        const opts = list.filter(isRecord).map((r) => ({ id: String(r.id ?? ""), label: rowTitle(r) })).filter((o) => o.id);
        optionCache.set(path, { at: Date.now(), items: opts });
        if (alive) setItems(opts);
      })
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [api, path]);
  return items;
};

/* ---------- value viewers ---------- */

const Value: React.FC<{ v: unknown; depth?: number }> = ({ v, depth = 0 }) => {
  if (v === null || v === undefined || v === "") return <span className={styles.no}>–</span>;
  if (typeof v === "boolean") return <span className={v ? styles.yes : styles.no}>{v ? "Yes" : "No"}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className={styles.no}>–</span>;
    if (v.every((x) => typeof x !== "object" || x === null)) {
      return (
        <span>
          {v.map((x, i) => (
            <span key={i} className={styles.pill} style={{ marginRight: 3 }}>
              {scalarText(x)}
            </span>
          ))}
        </span>
      );
    }
    const objs = v.filter(isRecord);
    const cols = pickColumns(objs).slice(0, 6);
    if (depth > 2 || cols.length === 0) return <pre className={`${styles.pre} ${styles.mono}`}>{JSON.stringify(v, null, 1)}</pre>;
    return (
      <table className={`${styles.table} ${styles.nestedTable}`}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c}>{humanize(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {objs.slice(0, 50).map((r, i) => (
            <tr key={i} style={{ cursor: "default" }}>
              {cols.map((c) => (
                <td key={c}>{scalarText(r[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (isRecord(v)) {
    if (depth > 3) return <pre className={`${styles.pre} ${styles.mono}`}>{JSON.stringify(v, null, 1)}</pre>;
    return (
      <div className={styles.nested}>
        <KeyValues data={v} depth={depth + 1} />
      </div>
    );
  }
  return <span>{scalarText(v)}</span>;
};

const KeyValues: React.FC<{ data: Row; depth?: number }> = ({ data, depth = 0 }) => (
  <div className={styles.kv}>
    {Object.entries(data).map(([k, v]) => (
      <React.Fragment key={k}>
        <div className={styles.kvKey}>{humanize(k)}</div>
        <div className={styles.kvVal}>
          <Value v={v} depth={depth} />
        </div>
      </React.Fragment>
    ))}
  </div>
);

const DataTable: React.FC<{ rows: Row[]; onPick: (r: Row) => void; selectedId?: string | undefined }> = ({ rows, onPick, selectedId }) => {
  const cols = useMemo(() => pickColumns(rows), [rows]);
  const [sort, setSort] = useState<{ k: string; dir: 1 | -1 } | null>(null);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => {
      const x = a[sort.k];
      const y = b[sort.k];
      if (typeof x === "number" && typeof y === "number") return (x - y) * sort.dir;
      return String(x ?? "").localeCompare(String(y ?? "")) * sort.dir;
    });
  }, [rows, sort]);
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c} onClick={() => setSort((s) => (s?.k === c ? { k: c, dir: s.dir === 1 ? -1 : 1 } : { k: c, dir: 1 }))}>
              {humanize(c)}
              {sort?.k === c ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((r, i) => (
          <tr key={String(r.id ?? i)} className={selectedId && r.id === selectedId ? styles.rowOn : ""} onClick={() => onPick(r)}>
            {cols.map((c) => {
              const v = r[c];
              return (
                <td key={c} title={typeof v === "string" ? v : undefined}>
                  {typeof v === "boolean" ? <span className={v ? styles.yes : styles.no}>{v ? "Yes" : "No"}</span> : scalarText(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ---------- forms ---------- */

interface FormField extends FieldDef {
  where: "b" | "q" | "p";
  file?: boolean;
}
type FormVals = Record<string, string | boolean>;
const keyOf = (f: FormField) => `${f.where}.${f.n}`;

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const initialValue = (f: FormField, initial: Row | undefined): string | boolean => {
  const raw = initial ? initial[f.n] : undefined;
  if (f.t === "b") return typeof raw === "boolean" ? raw : f.d === true;
  if (raw !== undefined && raw !== null) {
    if (f.t === "dt" && typeof raw === "string") return toLocalInput(raw);
    if (f.t === "d" && typeof raw === "string") return raw.slice(0, 10);
    if (Array.isArray(raw)) return raw.every((x) => typeof x !== "object") ? raw.join(", ") : JSON.stringify(raw, null, 1);
    if (typeof raw === "object") return JSON.stringify(raw, null, 1);
    return String(raw);
  }
  if (f.d !== undefined) return String(f.d);
  return "";
};

interface Collected {
  path: Vals;
  query: Record<string, string | number | boolean>;
  body: Row | undefined;
  file: File | null;
}
const collect = (fields: FormField[], vals: FormVals, hasBody: boolean, file: File | null): Collected | string => {
  const path: Vals = {};
  const query: Record<string, string | number | boolean> = {};
  const body: Row = {};
  for (const f of fields) {
    const raw = vals[keyOf(f)];
    let out: unknown;
    if (f.t === "b") out = raw === true || raw === "true";
    else {
      const s = typeof raw === "string" ? raw.trim() : "";
      if (s === "") {
        if (f.r) return `${humanize(f.n)} is required.`;
        continue;
      }
      if (f.t === "n") {
        const n = Number(s);
        if (Number.isNaN(n)) return `${humanize(f.n)} must be a number.`;
        out = n;
      } else if (f.t === "dt") {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return `${humanize(f.n)} is not a valid date.`;
        out = d.toISOString();
      } else if (f.t === "sa" || f.t === "ga") out = s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
      else if (f.t === "na") {
        const nums = s.split(/[,\n]/).map((x) => x.trim()).filter(Boolean).map(Number);
        if (nums.some(Number.isNaN)) return `${humanize(f.n)} must be numbers.`;
        out = nums;
      } else if (f.t === "j") {
        try {
          out = JSON.parse(s);
        } catch {
          return `${humanize(f.n)} must be valid JSON.`;
        }
      } else out = s;
    }
    if (f.where === "p") path[f.n] = String(out);
    else if (f.where === "q") query[f.n] = Array.isArray(out) ? out.join(",") : (out as string | number | boolean);
    else body[f.n] = out;
  }
  if (file === null && fields.some((f) => f.file)) return "Choose a file.";
  return { path, query, body: hasBody ? body : undefined, file };
};

const FieldInput: React.FC<{ api: ApiOptions; f: FormField; value: string | boolean; onChange: (v: string | boolean) => void; onFile: (f: File | null) => void }> = ({ api, f, value, onChange, onFile }) => {
  const ref = f.t === "g" ? refPath(f.n) : null;
  const options = useOptions(api, ref);
  const text = typeof value === "string" ? value : "";
  if (f.file) return <input className={styles.input} type="file" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />;
  if (f.t === "b")
    return (
      <label className={styles.check}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        <span>{value === true ? "Yes" : "No"}</span>
      </label>
    );
  if (f.o && f.t === "s")
    return (
      <select className={styles.input} value={text} onChange={(e) => onChange(e.target.value)}>
        {!f.r && <option value="">–</option>}
        {f.o.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        {text && !f.o.includes(text) && <option value={text}>{text}</option>}
      </select>
    );
  if (ref && options && options.length > 0)
    return (
      <select className={styles.input} value={text} onChange={(e) => onChange(e.target.value)}>
        <option value="">–</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
        {text && !options.some((o) => o.id === text) && <option value={text}>{text}</option>}
      </select>
    );
  if (f.t === "sa" || f.t === "ga" || f.t === "na" || f.t === "j")
    return <textarea className={styles.input} rows={f.t === "j" ? 4 : 2} value={text} placeholder={f.t === "j" ? "JSON" : "comma or new line separated"} onChange={(e) => onChange(e.target.value)} />;
  const type = f.t === "n" ? "number" : f.t === "dt" ? "datetime-local" : f.t === "d" ? "date" : "text";
  return <input className={styles.input} type={type} step={f.t === "n" ? "any" : undefined} value={text} placeholder={f.t === "g" ? "id" : ""} onChange={(e) => onChange(e.target.value)} />;
};

const FormDialog: React.FC<{
  api: ApiOptions;
  title: string;
  note?: string | undefined;
  fields: FormField[];
  initial?: Row | undefined;
  hasBody: boolean;
  submitLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onSubmit: (c: Collected) => Promise<void>;
}> = ({ api, title, note, fields, initial, hasBody, submitLabel, danger, onCancel, onSubmit }) => {
  const [vals, setVals] = useState<FormVals>(() => Object.fromEntries(fields.map((f) => [keyOf(f), initialValue(f, f.where === "b" ? initial : undefined)])));
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = collect(fields, vals, hasBody, file);
    if (typeof c === "string") {
      setError(c);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(c);
    } catch (err) {
      setError(errText(err));
      setBusy(false);
    }
  };
  return (
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <form className={`${styles.dialog} ${fields.length > 8 ? styles.dialogWide : ""}`} role="dialog" aria-modal="true" aria-label={title} onSubmit={submit}>
        <h3>{title}</h3>
        <div className={styles.dialogBody}>
          {note && <div className={styles.help}>{note}</div>}
          {fields.map((f) => (
            <label key={keyOf(f)} className={f.t === "b" ? `${styles.field} ${styles.check}` : styles.field}>
              <span className={styles.label}>
                {humanize(f.n)}
                {f.r ? <span className={styles.req}> *</span> : null}
              </span>
              <FieldInput api={api} f={f} value={vals[keyOf(f)] ?? ""} onChange={(v) => setVals((s) => ({ ...s, [keyOf(f)]: v }))} onFile={setFile} />
              {f.h && <span className={styles.help}>{f.h}</span>}
            </label>
          ))}
          {error && <div className={styles.formError}>{error}</div>}
        </div>
        <div className={styles.dialogFoot}>
          <button type="button" className={styles.btn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={`${styles.btn} ${danger ? styles.btnDanger : styles.btnPrimary}`} disabled={busy}>
            {busy ? "Working…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

const ResultDialog: React.FC<{ title: string; data: unknown; onClose: () => void }> = ({ title, data, onClose }) => (
  <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className={`${styles.dialog} ${styles.dialogWide}`} role="dialog" aria-modal="true" aria-label={title}>
      <h3>{title}</h3>
      <div className={styles.dialogBody}>
        {hasSecret(data) && <div className={styles.help}>Copy any key or secret now: it is shown only once.</div>}
        {isRecord(data) ? <KeyValues data={data} /> : <Value v={data} />}
      </div>
      <div className={styles.dialogFoot}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  </div>
);

/* ---------- section analysis: which endpoint plays which role ---------- */

interface Analysis {
  list: EndpointDef | undefined;
  create: EndpointDef | undefined;
  detail: EndpointDef | undefined;
  chips: EndpointDef[];
  rowViews: EndpointDef[];
  rowActions: EndpointDef[];
  actions: EndpointDef[];
}
const analyse = (section: SectionDef): Analysis => {
  const eps = section.eps;
  const lists = eps.filter((e) => e.m === "GET" && (e.k === "paged" || e.k === "list"));
  const list = [...lists].sort((a, b) => placeholders(a.p).length - placeholders(b.p).length || a.p.length - b.p.length)[0];
  const isRow = (e: EndpointDef) => !!list && e !== list && e.p.startsWith(list.p + "/{");
  const rowLevel = eps.filter(isRow);
  const detail = list ? rowLevel.find((e) => e.m === "GET" && e.k === "obj" && e.p.replace(/\{\w+\}$/, "") === list.p + "/" && placeholders(e.p).length === placeholders(list.p).length + 1) : undefined;
  const create = list ? eps.find((e) => e.m === "POST" && e.p === list.p) : undefined;
  return {
    list,
    create,
    detail,
    chips: eps.filter((e) => e.m === "GET" && !isRow(e)),
    rowViews: rowLevel.filter((e) => e.m === "GET" && e !== detail),
    rowActions: rowLevel.filter((e) => e.m !== "GET"),
    actions: eps.filter((e) => e.m !== "GET" && !isRow(e) && e !== create),
  };
};
const lastSegment = (p: string) => {
  const segs = p.split("/").filter((s) => s && !s.startsWith("{") && s !== "api");
  return segs[segs.length - 1] ?? "";
};
const chipLabel = (e: EndpointDef, all: EndpointDef[], list: EndpointDef | undefined) => {
  if (e === list) return "List";
  const base = humanize(lastSegment(e.p).replace(/\.\w+$/, ""));
  return all.filter((x) => x !== list && humanize(lastSegment(x.p)) === base).length > 1 ? `${base} (${humanize(e.a)})` : base;
};
const actionLabel = (e: EndpointDef) => {
  if (e.a === "Create") return "New";
  if (e.a === "Update" || (e.m === "PUT" && e.a.startsWith("Update"))) return e.a === "Update" ? "Edit" : humanize(e.a);
  if (e.a === "Delete") return "Delete";
  return humanize(e.a);
};
const isDanger = (e: EndpointDef) => e.m === "DELETE" || /(delete|cancel|suspend|revoke|purge|erase|reject|remove|disable|close|reset|decline)/i.test(e.a);

/* ---------- the action runner context ---------- */

interface Pending {
  ep: EndpointDef;
  vals: Vals;
  initial?: Row | undefined;
  label: string;
}

/* ---------- one section ---------- */

const SectionView: React.FC<{ api: ApiOptions; section: SectionDef; query: string }> = ({ api, section, query }) => {
  const an = useMemo(() => analyse(section), [section]);
  const [chipIdx, setChipIdx] = useState(0);
  const ep = an.chips[chipIdx];
  const [pathVals, setPathVals] = useState<Vals>({});
  const [filterDraft, setFilterDraft] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [auto, setAuto] = useState(/supervisor|live/i.test(section.key));
  const [menu, setMenu] = useState(false);
  const [data, setData] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<{ row: Row; vals: Vals } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [result, setResult] = useState<{ title: string; data: unknown } | null>(null);
  const [toast, setToast] = useState<{ msg: string; id: number } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);
  const notify = useCallback((msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToast({ msg, id: Date.now() });
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  const needs = ep ? placeholders(ep.p) : [];
  const missing = needs.filter((n) => !pathVals[n]);
  const isList = !!ep && ep === an.list;

  /* switching view resets paging / filters */
  useEffect(() => {
    setPage(1);
    setFilters({});
    setFilterDraft({});
    setFiltersOpen(false);
    setData(undefined);
    setError(null);
    setSel(null);
  }, [chipIdx]);
  useEffect(() => setPage(1), [query]);

  /* load the active view */
  useEffect(() => {
    if (!ep || ep.k === "file" || missing.length > 0) return;
    const ac = new AbortController();
    setLoading(true);
    const params: Record<string, string | number | boolean | undefined> = { ...filters };
    if (ep.k === "paged") {
      params.page = page;
      params.pageSize = PAGE_SIZE;
      if (query && ep.qp?.includes("search")) params.search = query;
    }
    apiCall<unknown>(api, "GET", fillPath(ep.p, pathVals), { params, signal: ac.signal })
      .then((d) => {
        if (ac.signal.aborted) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!ac.signal.aborted) setError(errText(e));
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ep, JSON.stringify(pathVals), JSON.stringify(filters), page, query, tick]);

  useEffect(() => {
    if (!auto) return;
    const id = window.setInterval(() => setTick((t) => t + 1), AUTO_MS);
    return () => window.clearInterval(id);
  }, [auto]);

  /* rows of the active view */
  const rows: Row[] = useMemo(() => {
    if (Array.isArray(data)) return data.filter(isRecord);
    if (isRecord(data) && Array.isArray(data.items)) return data.items.filter(isRecord);
    return [];
  }, [data]);
  const total = isRecord(data) && typeof data.total === "number" ? data.total : rows.length;
  const localFiltered = useMemo(() => {
    if (!query || ep?.k === "paged") return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, query, ep]);
  const clientPaged = ep?.k === "list";
  const shown = clientPaged ? localFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : localFiltered;
  const count = clientPaged ? localFiltered.length : total;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  /* ----- running an endpoint (forms, confirmation, download, upload) ----- */
  const rowVals = (row: Row, e: EndpointDef): Vals => {
    const vals: Vals = { ...pathVals };
    for (const n of placeholders(e.p)) if (!vals[n]) {
      const v = row[n] ?? row.id;
      if (v !== undefined && v !== null) vals[n] = String(v);
    }
    return vals;
  };
  const run = (e: EndpointDef, vals: Vals, initial?: Row) => setPending({ ep: e, vals, initial, label: actionLabel(e) });

  const execute = async (p: Pending, c: Collected) => {
    const vals: Vals = { ...p.vals, ...c.path };
    const path = fillPath(p.ep.p, vals);
    if (p.ep.x && c.file) {
      const out = await apiUpload<unknown>(api, path, c.file);
      afterRun(p, out);
      return;
    }
    if (p.ep.k === "file") {
      await apiDownload(api, p.ep.m, path, `${lastSegment(p.ep.p) || "download"}`, c.query, c.body);
      setPending(null);
      notify("Download started");
      return;
    }
    const out = await apiCall<unknown>(api, p.ep.m, path, { params: c.query, body: c.body });
    afterRun(p, out);
  };
  const afterRun = (p: Pending, out: unknown) => {
    setPending(null);
    setTick((t) => t + 1);
    if (p.ep.m === "DELETE") setSel(null);
    const informative = out !== undefined && out !== null && (hasSecret(out) || (!/^(Create|Update|Delete)/.test(p.ep.a) && p.ep.m !== "DELETE" && p.ep.k !== "none"));
    if (informative) setResult({ title: p.label, data: out });
    else notify(`${p.label}: done`);
  };

  const formFor = (p: Pending): { fields: FormField[]; hasBody: boolean } => {
    const fields: FormField[] = [];
    for (const n of placeholders(p.ep.p)) if (!p.vals[n]) fields.push({ n, t: "g", r: 1, where: "p" });
    for (const f of p.ep.q ?? []) fields.push({ ...f, where: "q" });
    for (const f of p.ep.b ?? []) fields.push({ ...f, where: "b" });
    if (p.ep.x) fields.push({ n: "file", t: "s", r: 1, where: "p", file: true });
    return { fields, hasBody: !!p.ep.b };
  };

  /* silent path: an action with nothing to fill in is only confirmed */
  const pendingForm = pending ? formFor(pending) : null;

  /* ----- views ----- */
  const filterFields = (ep?.q ?? []).filter((f) => !needs.includes(f.n));
  const activeFilters = Object.values(filters).filter(Boolean).length;

  const openRow = (r: Row) => {
    if (isList && ep) setSel({ row: r, vals: rowVals(r, ep) });
    else setResult({ title: rowTitle(r), data: r });
  };

  const runPending = pending;
  return (
    <>
      <div className={styles.bar}>
        <div className={styles.chips}>
          {an.chips.map((c, i) => (
            <button key={c.p + c.a} type="button" className={`${styles.chip} ${i === chipIdx ? styles.chipOn : ""}`} onClick={() => setChipIdx(i)}>
              {chipLabel(c, an.chips, an.list)}
            </button>
          ))}
          {an.chips.length === 0 && <span className={styles.hint}>This section only has actions.</span>}
        </div>
        {filterFields.length > 0 && (
          <button type="button" className={`${styles.btn} ${filtersOpen || activeFilters ? styles.chipOn : ""}`} onClick={() => setFiltersOpen((o) => !o)}>
            Filters{activeFilters ? ` (${activeFilters})` : ""}
          </button>
        )}
        <label className={styles.toggle}>
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
          Auto refresh
        </label>
        <button type="button" className={styles.btn} onClick={() => setTick((t) => t + 1)} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        {an.actions.length > 0 && (
          <div className={styles.menuWrap}>
            <button type="button" className={styles.btn} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
              Actions ▾
            </button>
            {menu && (
              <div className={styles.menu} role="menu">
                {an.actions.map((a) => (
                  <button
                    key={a.m + a.p}
                    type="button"
                    role="menuitem"
                    className={`${styles.menuItem} ${isDanger(a) ? styles.danger : ""}`}
                    onClick={() => {
                      setMenu(false);
                      run(a, { ...pathVals });
                    }}
                  >
                    {actionLabel(a)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {an.create && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => run(an.create as EndpointDef, { ...pathVals })}>
            + New
          </button>
        )}
      </div>

      {(needs.length > 0 || (filtersOpen && filterFields.length > 0)) && (
        <div className={styles.params}>
          {needs.map((n) => (
            <ParamInput key={n} api={api} name={n} value={pathVals[n] ?? ""} onCommit={(v) => setPathVals((s) => ({ ...s, [n]: v }))} />
          ))}
          {filtersOpen &&
            filterFields.map((f) => (
              <label key={f.n} className={styles.field}>
                <span className={styles.label}>{humanize(f.n)}</span>
                {f.o ? (
                  <select className={`${styles.input} ${styles.inputSm}`} value={filterDraft[f.n] ?? ""} onChange={(e) => setFilterDraft((s) => ({ ...s, [f.n]: e.target.value }))}>
                    <option value="">Any</option>
                    {f.o.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                ) : f.t === "b" ? (
                  <select className={`${styles.input} ${styles.inputSm}`} value={filterDraft[f.n] ?? ""} onChange={(e) => setFilterDraft((s) => ({ ...s, [f.n]: e.target.value }))}>
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    className={`${styles.input} ${styles.inputSm}`}
                    type={f.t === "n" ? "number" : f.t === "dt" ? "datetime-local" : f.t === "d" ? "date" : "text"}
                    value={filterDraft[f.n] ?? ""}
                    onChange={(e) => setFilterDraft((s) => ({ ...s, [f.n]: e.target.value }))}
                  />
                )}
              </label>
            ))}
          {filtersOpen && (
            <>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={() => {
                  const next: Record<string, string> = {};
                  for (const f of filterFields) {
                    const v = (filterDraft[f.n] ?? "").trim();
                    if (v) next[f.n] = f.t === "dt" ? new Date(v).toISOString() : v;
                  }
                  setPage(1);
                  setFilters(next);
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => {
                  setFilterDraft({});
                  setFilters({});
                  setPage(1);
                }}
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}

      <div className={styles.body}>
        {!ep && <div className={styles.empty}>Use “Actions” to work with {section.label.toLowerCase()}.</div>}
        {ep && missing.length > 0 && <div className={styles.empty}>Choose {missing.map(humanize).join(" and ").toLowerCase()} above to load this view.</div>}
        {ep && missing.length === 0 && ep.k === "file" && (
          <div className={styles.empty}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => run(ep, { ...pathVals })}>
              Download {humanize(lastSegment(ep.p)).toLowerCase()}
            </button>
          </div>
        )}
        {ep && missing.length === 0 && ep.k !== "file" && error && (
          <div className={styles.error}>
            {error}{" "}
            <button type="button" className={styles.btn} onClick={() => setTick((t) => t + 1)}>
              Try again
            </button>
          </div>
        )}
        {ep && missing.length === 0 && ep.k !== "file" && !error && data === undefined && <div className={styles.empty}>Loading…</div>}
        {ep && missing.length === 0 && ep.k !== "file" && !error && data !== undefined && (ep.k === "paged" || ep.k === "list") && (
          shown.length === 0 ? <div className={styles.empty}>Nothing here yet.</div> : <DataTable rows={shown} onPick={openRow} selectedId={sel && typeof sel.row.id === "string" ? sel.row.id : undefined} />
        )}
        {ep && missing.length === 0 && !error && data !== undefined && (ep.k === "obj" || ep.k === "none") && (isRecord(data) ? <KeyValues data={data} /> : data === null ? <div className={styles.empty}>Nothing to show.</div> : <div className={styles.kvVal}><Value v={data} /></div>)}

        {sel && isList && (
          <Drawer
            api={api}
            an={an}
            row={sel.row}
            vals={sel.vals}
            onClose={() => setSel(null)}
            onRun={(e, detail) => run(e, rowVals(sel.row, e), detail ?? sel.row)}
            tick={tick}
          />
        )}
      </div>

      {(ep?.k === "paged" || ep?.k === "list") && data !== undefined && (
        <div className={styles.foot}>
          <span>
            {count === 0 ? "No results" : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, count)} of ${count}`}
          </span>
          <span className={styles.pager}>
            <button type="button" className={styles.btn} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span>
              Page {page} / {pages}
            </span>
            <button type="button" className={styles.btn} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </span>
        </div>
      )}

      {runPending && pendingForm && pendingForm.fields.length > 0 && (
        <FormDialog
          api={api}
          title={runPending.label}
          note={runPending.ep.h}
          fields={pendingForm.fields}
          initial={runPending.initial}
          hasBody={pendingForm.hasBody}
          submitLabel={runPending.label}
          danger={isDanger(runPending.ep)}
          onCancel={() => setPending(null)}
          onSubmit={(c) => execute(runPending, c)}
        />
      )}
      {runPending && pendingForm && pendingForm.fields.length === 0 && (
        <ConfirmDialog
          title={runPending.label}
          text={runPending.ep.h ?? `${runPending.ep.m} ${fillPath(runPending.ep.p, runPending.vals)}`}
          danger={isDanger(runPending.ep)}
          onCancel={() => setPending(null)}
          onConfirm={() => execute(runPending, { path: {}, query: {}, body: pendingForm.hasBody ? {} : undefined, file: null })}
        />
      )}
      {result && <ResultDialog title={result.title} data={result.data} onClose={() => setResult(null)} />}
      {toast && (
        <div key={toast.id} className={styles.toast} role="status">
          {toast.msg}
        </div>
      )}
      {/* closes the Actions menu on outside click */}
      {menu && <div style={{ position: "absolute", inset: 0, zIndex: 20 }} onMouseDown={() => setMenu(false)} />}
    </>
  );
};

const ConfirmDialog: React.FC<{ title: string; text: string; danger: boolean; onCancel: () => void; onConfirm: () => Promise<void> }> = ({ title, text, danger, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !busy && onCancel()}>
      <div className={styles.dialog} role="alertdialog" aria-modal="true" aria-label={title}>
        <h3>{title}</h3>
        <div className={styles.dialogBody}>
          <div>{text}</div>
          {error && <div className={styles.formError}>{error}</div>}
        </div>
        <div className={styles.dialogFoot}>
          <button type="button" className={styles.btn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${danger ? styles.btnDanger : styles.btnPrimary}`}
            disabled={busy}
            autoFocus
            onClick={() => {
              setBusy(true);
              onConfirm().catch((e: unknown) => {
                setError(errText(e));
                setBusy(false);
              });
            }}
          >
            {busy ? "Working…" : title}
          </button>
        </div>
      </div>
    </div>
  );
};

const ParamInput: React.FC<{ api: ApiOptions; name: string; value: string; onCommit: (v: string) => void }> = ({ api, name, value, onCommit }) => {
  const options = useOptions(api, refPath(name));
  const [draft, setDraft] = useState(value);
  if (options && options.length > 0)
    return (
      <label className={styles.field}>
        <span className={styles.label}>{humanize(name)}</span>
        <select className={`${styles.input} ${styles.inputSm}`} value={value} onChange={(e) => onCommit(e.target.value)}>
          <option value="">Choose…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  return (
    <label className={styles.field}>
      <span className={styles.label}>{humanize(name)}</span>
      <input
        className={`${styles.input} ${styles.inputSm}`}
        value={draft}
        placeholder="value"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onCommit(draft.trim())}
        onKeyDown={(e) => e.key === "Enter" && onCommit(draft.trim())}
      />
    </label>
  );
};

/* ---------- row drawer: details, sub-views and actions of one record ---------- */

const Drawer: React.FC<{
  api: ApiOptions;
  an: Analysis;
  row: Row;
  vals: Vals;
  tick: number;
  onClose: () => void;
  onRun: (e: EndpointDef, detail?: Row) => void;
}> = ({ api, an, row, vals, tick, onClose, onRun }) => {
  const [detail, setDetail] = useState<unknown>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [viewIdx, setViewIdx] = useState(-1);
  const [view, setView] = useState<unknown>(undefined);
  const [viewError, setViewError] = useState<string | null>(null);

  const detailVals = (e: EndpointDef): Vals | null => {
    const v: Vals = { ...vals };
    for (const n of placeholders(e.p)) {
      if (!v[n]) {
        const x = row[n] ?? row.id;
        if (x === undefined || x === null) return null;
        v[n] = String(x);
      }
    }
    return v;
  };

  useEffect(() => {
    setDetail(undefined);
    setError(null);
    setViewIdx(-1);
    setView(undefined);
    if (!an.detail) return;
    const v = detailVals(an.detail);
    if (!v) return;
    const ac = new AbortController();
    apiCall<unknown>(api, "GET", fillPath(an.detail.p, v), { signal: ac.signal })
      .then((d) => !ac.signal.aborted && setDetail(d))
      .catch((e: unknown) => !ac.signal.aborted && setError(errText(e)));
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, row, tick]);

  useEffect(() => {
    setView(undefined);
    setViewError(null);
    const e = an.rowViews[viewIdx];
    if (!e) return;
    const v = detailVals(e);
    if (!v) return;
    const ac = new AbortController();
    apiCall<unknown>(api, "GET", fillPath(e.p, v), { signal: ac.signal })
      .then((d) => !ac.signal.aborted && setView(d))
      .catch((x: unknown) => !ac.signal.aborted && setViewError(errText(x)));
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, viewIdx, row, tick]);

  const shown = isRecord(detail) ? detail : row;
  const viewRows = Array.isArray(view) ? view.filter(isRecord) : isRecord(view) && Array.isArray(view.items) ? view.items.filter(isRecord) : null;

  return (
    <aside className={styles.drawer} aria-label="Details">
      <div className={styles.drawerHead}>
        <span className={styles.drawerTitle}>{rowTitle(shown)}</span>
        <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
          ×
        </button>
      </div>
      {an.rowViews.length > 0 && (
        <div className={styles.bar} style={{ paddingBottom: 3 }}>
          <div className={styles.chips}>
            <button type="button" className={`${styles.chip} ${viewIdx === -1 ? styles.chipOn : ""}`} onClick={() => setViewIdx(-1)}>
              Details
            </button>
            {an.rowViews.map((v, i) => (
              <button key={v.p} type="button" className={`${styles.chip} ${viewIdx === i ? styles.chipOn : ""}`} onClick={() => setViewIdx(i)}>
                {humanize(lastSegment(v.p))}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className={styles.drawerBody}>
        {viewIdx === -1 ? (
          error ? (
            <div className={styles.error}>{error}</div>
          ) : an.detail && detail === undefined ? (
            <div className={styles.empty}>Loading…</div>
          ) : (
            <KeyValues data={shown} />
          )
        ) : viewError ? (
          <div className={styles.error}>{viewError}</div>
        ) : view === undefined ? (
          <div className={styles.empty}>Loading…</div>
        ) : viewRows ? (
          viewRows.length === 0 ? (
            <div className={styles.empty}>Nothing here yet.</div>
          ) : (
            <DataTable rows={viewRows} onPick={() => undefined} />
          )
        ) : isRecord(view) ? (
          <KeyValues data={view} />
        ) : (
          <div className={styles.kvVal}>
            <Value v={view} />
          </div>
        )}
      </div>
      {an.rowActions.length > 0 && (
        <div className={styles.drawerActions}>
          {an.rowActions.map((a) => (
            <button key={a.m + a.p} type="button" className={`${styles.btn} ${isDanger(a) ? styles.btnDanger : ""}`} onClick={() => onRun(a, isRecord(detail) ? detail : undefined)}>
              {actionLabel(a)}
            </button>
          ))}
        </div>
      )}
    </aside>
  );
};

/* ---------- the module page ---------- */

const ModulePage: React.FC<ModulePageProps> = ({ module, api, query }) => {
  const [sectionKey, setSectionKey] = useState(module.sections[0]?.key ?? "");
  const section = module.sections.find((s) => s.key === sectionKey) ?? module.sections[0];
  return (
    <div
      className={styles.root}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === "Escape" && (e.target as HTMLElement).closest(`.${styles.overlay}, .${styles.drawer}`)) {
          e.stopPropagation();
          e.nativeEvent.stopPropagation();
        }
      }}
    >
      <div className={styles.tabs} role="tablist" aria-label={module.label}>
        {module.sections.map((s) => (
          <button key={s.key} type="button" role="tab" aria-selected={s.key === section?.key} className={`${styles.tab} ${s.key === section?.key ? styles.tabOn : ""}`} onClick={() => setSectionKey(s.key)}>
            {s.label}
          </button>
        ))}
      </div>
      {section && <SectionView key={`${module.key}/${section.key}`} api={api} section={section} query={(query ?? "").trim()} />}
    </div>
  );
};

export default ModulePage;